
import React, { useState, useEffect } from 'react';
import { ViewState } from '../types';
import { BrandLogo } from './BrandIdentity';

interface NavbarProps {
  onNavigate: (view: ViewState) => void;
  onAuthMode: (mode: 'login' | 'register') => void;
  currentView: ViewState;
  isLoggedIn?: boolean;
  onLogout?: () => void;
  authMode?: 'login' | 'register';
}

export const Navbar: React.FC<NavbarProps> = ({ onNavigate, onAuthMode, currentView, isLoggedIn, onLogout, authMode }) => {
  const [favoriteCount, setFavoriteCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      const saved = localStorage.getItem('hoppy_favorites');
      if (saved) {
        setFavoriteCount(JSON.parse(saved).length);
      }
    };
    updateCount();
    window.addEventListener('storage', updateCount);
    window.addEventListener('favoritesUpdated', updateCount);
    return () => {
      window.removeEventListener('storage', updateCount);
      window.removeEventListener('favoritesUpdated', updateCount);
    };
  }, []);

  const handleNav = (view: ViewState) => {
    onNavigate(view);
  };

  const handleAuth = (mode: 'login' | 'register') => {
    onAuthMode(mode);
  };

  return (
    <div className="absolute top-4 md:top-6 left-1/2 -translate-x-1/2 z-[100] w-[95%] max-w-6xl">
      <nav className="bg-black/95 backdrop-blur-3xl border border-white/10 rounded-full px-4 md:px-6 py-2 md:py-2.5 flex justify-between items-center shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)]">
        {/* Logo Section */}
        <div 
          className="flex items-center cursor-pointer group origin-left shrink-0" 
          onClick={() => handleNav('landing')}
        >
          <BrandLogo size="md" light className="group-hover:scale-105 transition-transform" />
        </div>

        {/* Action Items */}
        <div className="flex items-center gap-1 sm:gap-4 md:gap-6">
          <div className="flex items-center">
            <button 
              onClick={() => handleNav('results')}
              className={`px-2 md:px-4 py-2 rounded-full text-[9px] sm:text-[10px] md:text-xs font-black tracking-widest transition-all uppercase ${currentView === 'results' ? 'text-amber-500' : 'text-gray-400 hover:text-white'}`}
            >
              Explore
            </button>
            {favoriteCount > 0 && (
              <span className="bg-amber-500 text-black text-[7px] md:text-[8px] font-black px-1.5 py-0.5 rounded-full -ml-1 md:-ml-2 mb-3 md:mb-4 animate-in zoom-in duration-300">
                {favoriteCount}
              </span>
            )}
          </div>
          
          <div className="hidden xs:block w-px h-3 md:h-4 bg-white/10" />
          
          {isLoggedIn ? (
            <>
              <button 
                onClick={() => handleNav('merchant-portal')}
                className={`px-2 md:px-0 text-[9px] sm:text-[10px] md:text-xs font-black tracking-widest transition-colors uppercase ${currentView === 'merchant-portal' ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`}
              >
                Dashboard
              </button>
              <button 
                onClick={onLogout}
                className="px-2 md:px-0 text-[9px] sm:text-[10px] md:text-xs font-black tracking-widest text-gray-400 hover:text-red-400 transition-colors uppercase"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={() => handleAuth('login')}
                className={`px-2 md:px-0 text-[9px] sm:text-[10px] md:text-xs font-black tracking-widest transition-colors uppercase ${currentView === 'merchant-auth' && authMode === 'login' ? 'text-amber-500' : 'text-gray-400 hover:text-amber-500'}`}
              >
                Login
              </button>
              <button 
                onClick={() => handleAuth('register')}
                className={`px-3 sm:px-5 md:px-7 py-2 md:py-2.5 rounded-full text-[9px] sm:text-[10px] md:text-xs font-black tracking-widest uppercase transition-all shadow-lg active:scale-95 shrink-0 ${currentView === 'merchant-auth' && authMode === 'register' ? 'bg-amber-500 text-black' : 'bg-white text-black hover:bg-amber-500'}`}
              >
                List<span className="hidden xs:inline"> Specials</span>
              </button>
            </>
          )}
        </div>
      </nav>
    </div>
  );
};
